import React from 'react';
import { connect } from 'react-redux';
import { Option, option } from 'ts-option';

import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/theme-chrome';
import 'ace-builds/src-noconflict/mode-sql';

import fixedEncodeURIComponent from 'common/js_utils/fixedEncodeURIComponent';
import { fetchTranslation } from 'common/locale';
import SoQLMode from 'common/components/SoQLEditor/SoQLAceMode';

import * as Actions from '../redux/actions';
import { AppState, OpenModalType } from '../redux/store';
import { MAX_FRONTEND_URL_SIZE } from 'common/utilities/Constants';
import { getCurrentDomain } from 'common/currentDomain';
import airbrake from 'common/airbrake';
import { ForgeButton, ForgeDialog, ForgeIcon, ForgeIconButton, ForgeScaffold, ForgeToolbar } from '@tylertech/forge-react';
import '../styles/saved-query-modal.scss';

const t = (k: string) => fetchTranslation(k, 'shared.explore_grid.saved_query_modal');

export type SavedQueryModalProps = SavedQueryModalStateProps & SavedQueryModalDispatchProps;
type SavedQueryModalStateProps = {
  isOpen: boolean;
  maybeSavedQueryText: Option<string>;
  maybeParentUid: Option<string>;
};
type SavedQueryModalDispatchProps = {
  onDismiss: () => void;
};

type Editor = any;

export function SavedQueryModal(props: SavedQueryModalProps) {
  const { onDismiss, isOpen, maybeSavedQueryText, maybeParentUid } = props;

  if (!maybeSavedQueryText.isDefined || !maybeParentUid.isDefined) {
    return null;
  }
  const parentUid = maybeParentUid.get;
  const savedQueryText = maybeSavedQueryText.get;

  let parentLink = `/d/${parentUid}/explore/query/${fixedEncodeURIComponent(savedQueryText)}`;
  const urlBase = 'https://' + (getCurrentDomain() || '');
  const urlLength = urlBase.length + parentLink.length;

  if (urlLength > MAX_FRONTEND_URL_SIZE) {
    const airbrakePayload = {
      error: `Unable to use url for link as it exceeds the allowed MAX_FRONTEND_URL_SIZE of ${MAX_FRONTEND_URL_SIZE}`,
      context: {
        url: urlBase + parentLink,
        urlLength
      }
    };

    try {
      airbrake.notify(airbrakePayload);
    } catch (err) {
      console.error(`Error notifying Airbrake: ${err}`, airbrakePayload);
    }

    parentLink = `/d/${parentUid}/explore/`;
  }

  const bootstrapEditor = (editor: Editor) => {
    editor.getSession().setMode(new SoQLMode());
  };

  const defaultEditorProps = {
    name: 'saved-query-editor',
    mode: 'sql',
    width: '100%',
    theme: 'chrome',
    fontSize: 14,
    minLines: 2,
    maxLines: 30,
    showGutter: false,
    showPrintMargin: false,
    focus: true,
    readOnly: true,
    wrapEnabled: true,
    onLoad: bootstrapEditor,
    lineHeight: '1.5',
    setOptions:{
      fontFamily: 'courier'
    }
  };

  return (
    <ForgeDialog
      on-forge-dialog-close={onDismiss}
      open={isOpen}
      id="saved-query-modal"
      aria-labelledby="saved-query-modal-title"
      aria-describedby="saved-query-modal-sub-heading"
      data-testid="saved-query-modal"
      >
      <ForgeScaffold>
        <ForgeToolbar slot="header">
          <h2 className="forge-typography--heading3" id="saved-query-modal-title" slot='start'>{t('title')}</h2>
          <ForgeIconButton slot="end" className="tyler-icons" onClick={onDismiss} aria-label={t('close')}>
            <ForgeIcon name="close" />
          </ForgeIconButton>
        </ForgeToolbar>

          <div slot="body" className='saved-query-modal-body'>
            <h3 id="saved-query-modal-sub-heading" className='forge-typography--subheading1'>{t('body')}</h3>
            <div className="saved-query-modal-code-window ">
              <AceEditor
                {...defaultEditorProps} readOnly={true} value={savedQueryText}
              />
            </div>
            <div className='parent-link-section'>
              <a className='forge-hyperlink' href={parentLink} target="_blank" rel="noopener noreferrer">{t('parent_link')}</a>
            </div>
          </div>
          <ForgeToolbar slot="footer">
            <ForgeButton slot="end" className="close-button" onClick={onDismiss}>
              {t('close')}
            </ForgeButton>
          </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
}

const mapStateToProps = (state: AppState): SavedQueryModalStateProps => {
  return {
    isOpen: state.openModal.type == OpenModalType.SAVED_QUERY,
    maybeSavedQueryText: option(state.view.queryString),
    maybeParentUid: state.parentInfo.mainParent.map(v => v.id)
  };
};

const mapDispatchToProps = (dispatcher: Actions.Dispatcher) => {
  return {
    onDismiss: () => dispatcher(Actions.closeModal())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedQueryModal);
